"use client";

import { env } from "@/env";
import { useSession } from "@/lib/auth-client";
import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { useEffect, useRef, useState } from "react";
import {
  installTestTracking,
  loadTrafficContext,
} from "@/lib/browser-traffic-analytics";
import {
  audienceProperties,
  readCookie,
  TEST_RUN_COOKIE,
  validTestRunId,
} from "@/lib/traffic-analytics";

function PostHogIdentity() {
  const { data: session, isPending } = useSession();
  const lastLinkedUserId = useRef<string | null>(null);

  useEffect(() => {
    if (isPending) return;
    const user = session?.user;
    if (!user) {
      if (lastLinkedUserId.current !== null) {
        lastLinkedUserId.current = null;
        let cancelled = false;
        posthog.reset();
        void loadTrafficContext().then((properties) => {
          if (!cancelled) posthog.register(properties);
        });
        return () => {
          cancelled = true;
        };
      }
      return;
    }
    const identityKey = `${user.id}:${user.isAnonymous === true}:${user.emailVerified}:${user.email}`;
    if (lastLinkedUserId.current === identityKey) return;
    if (
      lastLinkedUserId.current &&
      !lastLinkedUserId.current.startsWith(`${user.id}:`)
    )
      posthog.reset();
    posthog.register(
      audienceProperties(
        "unknown",
        validTestRunId(readCookie(document.cookie, TEST_RUN_COOKIE)),
      ),
    );
    let cancelled = false;

    void loadTrafficContext().then((properties) => {
      if (cancelled) return;
      posthog.register(properties);
      lastLinkedUserId.current = identityKey;

      const isAnonymous = user.isAnonymous === true;
      posthog.identify(user.id, {
        user_id: user.id,
        is_anonymous: isAnonymous,
        is_staff_account: properties.is_staff === true,
        is_test_account:
          properties.traffic_marker_source === "account_allowlist" &&
          properties.is_test === true,
        ...(isAnonymous ? {} : { email: user.email }),
      });

      if (isAnonymous) {
        posthog.capture("anonymous_session_linked", {
          user_id: user.id,
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [session?.user, isPending]);

  return null;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (env.NODE_ENV === "development") return;

    let cancelled = false;
    // Resolve audience before initialization so the automatic first pageview is
    // marked too. A failed lookup is unknown, and never interrupts the product.
    void loadTrafficContext().then((properties) => {
      if (cancelled) return;
      posthog.init(env.NEXT_PUBLIC_POSTHOG_KEY, {
        api_host: "/ingest",
        ui_host: "https://us.posthog.com",
        defaults: "2025-05-24",
        capture_exceptions: true, // This enables capturing exceptions using Error Tracking
        // Avoid unload-time beacon batches exceeding the browser keepalive budget.
        capture_pageleave: false,
        disable_session_recording: false,
        // Replay is useful on the account-creation path, but rrweb should not
        // compete with the initial render or serialize executable page scripts.
        __preview_deferred_init_extensions: true,
        session_recording: {
          maskAllInputs: true,
          slimDOMOptions: "all",
          collectFonts: false,
          recordCrossOriginIframes: false,
        },
        // Keep explicit consent choices in a first-party cookie so an opt-out
        // survives reloads in Safari as well as the other supported browsers.
        opt_out_capturing_persistence_type: "cookie",
        respect_dnt: true,
        opt_out_capturing_by_default:
          "globalPrivacyControl" in navigator &&
          navigator.globalPrivacyControl === true,
        loaded: (client) => {
          client.register(properties);
          installTestTracking(client);
        },
        before_send: (event) => {
          if (!event) return event;
          const testRun = validTestRunId(
            readCookie(document.cookie, TEST_RUN_COOKIE),
          );
          if (testRun)
            Object.assign(
              event.properties,
              audienceProperties("test", testRun),
            );
          else if (event.properties.traffic_marker_source === "browser_test") {
            Object.assign(
              event.properties,
              audienceProperties("unknown", null),
            );
          }
          return event;
        },
        debug: false,
      });

      // The npm build does not install itself on window like PostHog's script
      // snippet does. Expose this initialized instance so production operators can
      // run `posthog.opt_out_capturing()` directly from the browser console.
      Object.assign(window, { posthog });
      setInitialized(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (env.NODE_ENV === "development") return children;

  return (
    <PHProvider client={posthog}>
      {initialized ? <PostHogIdentity /> : null}
      {children}
    </PHProvider>
  );
}
